import * as React from "react"
import { useFlags } from "@modules/featureFlags"

type LocaleDictionaryValue = string | string[] | LocaleDictionary

type LocaleDictionary = {
  [k: string]: LocaleDictionaryValue
}

/**
 * This hooks allows to get a text copy based on a feature flag values
 * To make this work, "baseDictionary" should contain adhere to a structure like this:
 * {
 *   "loremMessage": "Lorem ipsum",
 *   "loremMessage::your-flag-key": "muspi meroL",
 * }
 *
 * Deep nesting is supported as long as the "::flag-key" suffix is applied to the deepest level
 * {
 *   "loremMessage": "Lorem ipsum",
 *   "loremMessage::your-flag-key": "muspi meroL",
 *   "author": {
 *     "name": "Cicero",
 *     "name::your-flag-key": "Marcus Tullius Cicero"
 *   }
 * }
 *
 * The hook returns an object of the same exact shape as the "baseDictionary",
 * but each key with a matching suffixed key will have its corresponding value replaced if the flag is enabled
 *
 * Note that the suffixed keys will still be present, although probably completely useless when using this hook
 */
export function useFlaggedDictionary<T extends LocaleDictionary>(
  baseDictionary: T,
  flagKey: string
): T {
  const { flags } = useFlags()

  return React.useMemo(() => {
    // No need to do anything if the flag is disabled
    if (!flags[flagKey]) {
      return baseDictionary
    }

    const flaggedDictionary: LocaleDictionary = {}
    // Traverse baseDictionary and fill flaggedDictionary
    createFlaggedDictionary(flaggedDictionary, baseDictionary, flagKey)

    // Due to how badly TypeScript handles objects, we are going to cast flaggedDictionary to T (using "as") before return
    return flaggedDictionary as T
  }, [flags[flagKey], baseDictionary])
}

function createFlaggedDictionary<T extends LocaleDictionary>(
  flaggedDictionary: LocaleDictionary,
  baseDictionary: T,
  flagKey: string
): void {
  const dictionaryKeys = Object.keys(baseDictionary)

  if (dictionaryKeys.length === 0) {
    return
  }

  for (const textKey of dictionaryKeys) {
    const baseText = baseDictionary[textKey]

    if (typeof baseText === `object` && !Array.isArray(baseText)) {
      // Current value is an object, we need to delve deeper
      const subDictionary = {}
      createFlaggedDictionary(
        subDictionary,
        baseText as Exclude<LocaleDictionary, string>,
        flagKey
      )
      flaggedDictionary[textKey] = subDictionary
    } else {
      const textForFlag = baseDictionary[`${textKey}::${flagKey}`]

      // Use either the flagged variant (textForFlag) or fallback to the original text
      flaggedDictionary[textKey] = textForFlag || baseText
    }
  }
}
